import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, GraduationCap, Users, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import HeroImage from "@/components/HeroImage";
import Collapsible from "@/components/Collapsible";

const DEFAULTS = {
  eyebrow: "Organisation académique",
  title: "Directions du Secrétariat Général à la Recherche",
  banner_image: "/media/hero-university.jpg",
  intro:
    "Le Secrétariat Général à la Recherche s'appuie sur trois grandes Directions qui structurent l'activité scientifique de l'UNISIC : la conservation et la diffusion du savoir, la formation doctorale, et le suivi du personnel académique et scientifique.",
};

const DIRECTIONS = [
  {
    key: "biblio-musees",
    to: "/directions/bibliotheques-musees",
    Icon: BookOpen,
    color: "#0A2E52",
    eyebrow: "Recherche, conservation et diffusion",
    title: "Direction de la Recherche, des Bibliothèques et Musées",
    desc:
      "Recherche scientifique (laboratoires CECOM), bibliothèques centrale et numérique, PUSIC, formation et patrimoine muséal — un pôle complet dédié aux ressources scientifiques.",
    banner: "/media/library-shelves.jpg",
  },
  {
    key: "ecole-doctorale",
    to: "/directions/ecole-doctorale",
    Icon: GraduationCap,
    color: "#D4AF37",
    eyebrow: "Formation à la recherche",
    title: "École Doctorale",
    desc:
      "Formation, encadrement et accompagnement des chercheurs doctorants dans un environnement académique exigeant.",
    banner: "/media/university-hall.jpg",
  },
  {
    key: "suivi-encadrement",
    to: "/directions/suivi-encadrement",
    Icon: Users,
    color: "#144C86",
    eyebrow: "Personnel académique et scientifique",
    title: "Direction de Suivi et Encadrement du Personnel Académique et Scientifique",
    desc:
      "Gestion et suivi de carrière, encadrement pédagogique et scientifique, évaluation et développement professionnel du personnel.",
    banner: "/media/research-lab.jpg",
  },
];

export default function Directions() {
  const [content, setContent] = useState(DEFAULTS);
  useEffect(() => {
    api.get("/content/directions.home").then((r) => setContent({ ...DEFAULTS, ...(r.data || {}) })).catch(() => {});
  }, []);

  return (
    <div data-testid="directions-hub">
      <section className="relative h-[46vh] min-h-[340px] w-full overflow-hidden">
        <HeroImage src={content.banner_image} />
        <div className="absolute inset-0 hero-veil" />
        <div className="relative h-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-14">
          <div className="eyebrow" style={{ color: "#E5C158" }}>{content.eyebrow}</div>
          <h1 className="mt-3 text-white text-4xl sm:text-5xl font-bold tracking-tight max-w-4xl">
            {content.title}
          </h1>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-unisic-gold" />
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="eyebrow eyebrow-dark">Présentation</div>
        <span className="divider-gold mt-3" />
        <Collapsible text={content.intro} className="mt-6 max-w-4xl" testid="directions-hub-intro" />

        <div className="mt-14">
          <div className="eyebrow eyebrow-dark">Trois Directions</div>
          <span className="divider-gold mt-3" />
          <div className="mt-8 grid gap-8 lg:grid-cols-3">
            {DIRECTIONS.map((d) => (
              <Link
                key={d.key}
                to={d.to}
                className="group paper-card card-hover overflow-hidden flex flex-col"
                data-testid={`direction-card-${d.key}`}
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={d.banner} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-sm mb-2" style={{ background: d.color, color: "#fff" }}>
                      <d.Icon className="w-5 h-5" />
                    </div>
                    <div className="text-[11px] tracking-[0.28em] uppercase text-white/85 font-semibold">
                      {d.eyebrow}
                    </div>
                    <h3 className="text-white text-lg lg:text-xl font-bold mt-1 leading-tight">{d.title}</h3>
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <p className="text-sm text-slate-600 leading-relaxed flex-1">{d.desc}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-unisic-blue font-semibold text-sm group-hover:text-unisic-gold transition-colors">
                    Découvrir la Direction <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
